import type { ReactNode } from 'react';

/**
 * Bộ hình vẽ của panel.
 *
 * ── Vì sao thay emoji ──────────────────────────────────────────────────────
 *
 * Trước lượt này panel dùng emoji làm icon: 📈 cho nhóm Theo dõi, ☰ cho nút
 * menu, ☀/☾ cho nút đổi chủ đề, ▲▼ cho chiều tăng giảm. Emoji không phải hình
 * vẽ giao diện — chúng do PHÔNG CHỮ CỦA HỆ ĐIỀU HÀNH vẽ ra, nên:
 *
 *  • mỗi máy hiện một kiểu, và trên Windows là bộ nhiều màu, tô đầy, viền dày —
 *    đứng cạnh một giao diện nét mảnh thì chúng là thứ duy nhất trông như dán
 *    vào từ một chương trình khác;
 *  • chúng không nhận `currentColor`, nên không bao giờ theo được màu của dòng
 *    chữ bên cạnh, không mờ đi cùng trạng thái `disabled`, không đổi theo chủ đề;
 *  • kích thước của chúng bám theo cỡ chữ chứ không theo ô chứa, nên căn hàng
 *    với chữ luôn lệch một hai pixel ở đâu đó.
 *
 * ── Vì sao tự vẽ, không cài thư viện ───────────────────────────────────────
 *
 * Panel cần đúng chừng ba chục hình. Một gói icon mang theo hơn một nghìn hình,
 * một lớp component, và một phụ thuộc nữa phải nâng cấp — để dùng 2% của nó.
 * Cả kho này cũng đang theo đúng quy ước đó (`lib/nav.ts` ghi rõ "không có thư
 * viện icon nào"). Hình học theo lối Phosphor thin: hộp 24×24, nét 1.5, đầu và
 * mối nối bo tròn — đủ để cả bộ trông ra một bộ.
 *
 * ── Một API, không phải hai ────────────────────────────────────────────────
 *
 * Chỉ có `<Icon name="..." />`, không kèm bản xuất từng hình một. `lib/nav.ts`
 * dù sao cũng cần tra hình BẰNG CHUỖI (bản đồ điều hướng là dữ liệu, và
 * `nav.test.ts` chạy bằng `node --test` trên dữ liệu thuần, không dựng nổi một
 * cây React). Ship cả hai API thì chắc chắn nửa mã nguồn dùng cái này, nửa kia
 * dùng cái kia.
 */

const GLYPHS = {
  // ── Mười nhóm điều hướng (lib/nav.ts) ────────────────────────────────────
  'chart-line': (
    <>
      <path d="M4 4v14.5a1.5 1.5 0 0 0 1.5 1.5H20" />
      <path d="m7.5 15 3.8-4.6 3.1 2.6L19 6.6" />
    </>
  ),
  'chat-dots': (
    <>
      <path d="M20.5 12a8 8 0 0 1-11.9 7L4 20.3l1.3-4.6A8 8 0 1 1 20.5 12Z" />
      <circle cx="8.8" cy="12" r=".95" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="12" r=".95" fill="currentColor" stroke="none" />
      <circle cx="16.2" cy="12" r=".95" fill="currentColor" stroke="none" />
    </>
  ),
  /*
   * Vết khía của vé nông (r=1.5) và nằm giữa một thân thấp.
   *
   * Bản đầu dùng r=2.4 trên một thân cao 12: hai vết khía ăn 40% chiều cao và
   * gần chạm nhau, nên ở 16px cái vé đọc ra thành một chiếc nơ.
   */
  ticket: (
    <>
      <path d="M3.4 10.5V8.3a1.2 1.2 0 0 1 1.2-1.2h14.8a1.2 1.2 0 0 1 1.2 1.2v2.2a1.5 1.5 0 0 0 0 3v2.2a1.2 1.2 0 0 1-1.2 1.2H4.6a1.2 1.2 0 0 1-1.2-1.2v-2.2a1.5 1.5 0 0 0 0-3Z" />
      <path d="M14.9 7.4v9.2" strokeDasharray="1.8 2" />
    </>
  ),
  'shield-check': (
    <>
      <path d="M12 20.8s6.9-3.1 6.9-8.8V6.3L12 3.6 5.1 6.3v5.7c0 5.7 6.9 8.8 6.9 8.8Z" />
      <path d="m8.9 11.9 2.3 2.3 4.1-4.6" />
    </>
  ),
  'arrows-clockwise': (
    <>
      <path d="M4.6 12a7.4 7.4 0 0 1 12.6-5.2L20 9.5" />
      <path d="M20 4.6v5h-5" />
      <path d="M19.4 12a7.4 7.4 0 0 1-12.6 5.2L4 14.5" />
      <path d="M4 19.4v-5h5" />
    </>
  ),
  'users-three': (
    <>
      <circle cx="12" cy="9.4" r="3" />
      <path d="M7.7 18.8a5 5 0 0 1 8.6 0" />
      <circle cx="4.7" cy="10.9" r="2.2" />
      <path d="M2 17.3a4.1 4.1 0 0 1 3.6-2.4" />
      <circle cx="19.3" cy="10.9" r="2.2" />
      <path d="M22 17.3a4.1 4.1 0 0 0-3.6-2.4" />
    </>
  ),
  /*
   * Nơ nhỏ và thấp. Bản đầu để hai vòng nơ bán kính 2.2 nhô lên tới y=3, và ở
   * cỡ nhỏ chúng đọc ra thành một bông hoa đặt trên một cái hộp.
   */
  gift: (
    <>
      <path d="M5 12.2h14v6.9a1.1 1.1 0 0 1-1.1 1.1H6.1A1.1 1.1 0 0 1 5 19.1Z" />
      <rect x="3.4" y="8.5" width="17.2" height="3.7" rx=".9" />
      <path d="M12 8.5V20.2" />
      <path d="M12 8.5H9.5a1.75 1.75 0 1 1 0-3.5c1.6 0 2.5 3.5 2.5 3.5Z" />
      <path d="M12 8.5h2.5a1.75 1.75 0 1 0 0-3.5C12.9 5 12 8.5 12 8.5Z" />
    </>
  ),
  /*
   * Bánh răng là MỘT đường viền có sáu răng hình thang, không phải một vòng
   * tròn cộng sáu tia.
   *
   * Bản đầu vẽ kiểu tia, và nó trùng hình với `sun` tới mức không phân biệt
   * được — mà hai hình đó đứng cách nhau vài chục pixel trên cùng một thanh bên
   * (nhóm "Hệ thống" và nút đổi chủ đề). Răng hình thang thì hẹp ở đỉnh, rộng ở
   * chân, và đó là thứ duy nhất nói "bánh răng" chứ không phải "mặt trời".
   *
   * Toạ độ sinh bằng máy: 6 răng, bán kính chân 6.35 / đỉnh 9.05, nửa góc đỉnh
   * 12° và nửa góc chân 19.5°.
   */
  'gear-six': (
    <>
      <path d="M9.88 6.01L10.12 3.15L13.88 3.15L14.12 6.01A6.35 6.35 0 0 1 16.12 7.17L18.73 5.94L20.61 9.20L18.24 10.84A6.35 6.35 0 0 1 18.24 13.16L20.61 14.80L18.73 18.06L16.12 16.83A6.35 6.35 0 0 1 14.12 17.99L13.88 20.85L10.12 20.85L9.88 17.99A6.35 6.35 0 0 1 7.88 16.83L5.27 18.06L3.39 14.80L5.76 13.16A6.35 6.35 0 0 1 5.76 10.84L3.39 9.20L5.27 5.94L7.88 7.17A6.35 6.35 0 0 1 9.88 6.01Z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  /* Đế liền với thân, không phải một gạch rời: ở 16px một đường kẻ cách thân
     2px trông như một nét thừa chứ không như phần đế của vương miện. */
  crown: (
    <>
      <path d="M3.2 7.6 5.7 15.7h12.6l2.5-8.1-4.6 3.1L12 5.1l-4.2 5.6Z" />
      <path d="M5.7 15.7v2.1a1.1 1.1 0 0 0 1.1 1.1h10.4a1.1 1.1 0 0 0 1.1-1.1v-2.1" />
    </>
  ),
  /* Thẻ đeo bo góc + một chân dung nhỏ bên trong, cho nhóm "Chào mừng & vai
     trò" (thêm 15/08/2026): thẻ là "được cấp một thứ gắn với danh tính". */
  'id-badge': (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2.4" />
      <circle cx="12" cy="10.3" r="2.3" />
      <path d="M8 16.6a4 4 0 0 1 8 0" />
    </>
  ),

  // ── Lưới tính năng trang chủ công khai (content/landing.ts) ───────────────
  /* Đường xích đạo thẳng + hai cung kinh tuyến đối xứng qua tâm — cách vẽ quả
     địa cầu tối giản kinh điển, không cố vẽ cả lưới kinh vĩ tuyến dày đặc. */
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c-3 2.9-3 14.1 0 17" />
      <path d="M12 3.5c3 2.9 3 14.1 0 17" />
    </>
  ),
  /* Ngôi sao năm cánh đều, không phải cúp — toạ độ sinh bằng máy (bán kính
     đỉnh 8.5 / hõm 3.4, tỉ lệ 0.4) nên luôn đều cánh dù đổi cỡ hiển thị. */
  star: (
    <path d="M12 3.5 14 9.25 20.08 9.37 15.23 13.05 17 18.88 12 15.4 7 18.88 8.77 13.05 3.92 9.37 10 9.25Z" />
  ),

  // ── Khung panel ──────────────────────────────────────────────────────────
  list: <path d="M4 7h16M4 12h16M4 17h16" />,
  robot: (
    <>
      <rect x="4" y="8.2" width="16" height="11.8" rx="3" />
      <path d="M12 4.9v3.3" />
      <circle cx="12" cy="3.7" r="1.15" />
      <circle cx="9.1" cy="13.4" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14.9" cy="13.4" r="1.1" fill="currentColor" stroke="none" />
      <path d="M9.6 16.9h4.8" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.6v2.2M12 19.2v2.2M4.6 12H2.4M21.6 12h-2.2M6.4 6.4 4.85 4.85M19.15 19.15 17.6 17.6M17.6 6.4l1.55-1.55M4.85 19.15 6.4 17.6" />
    </>
  ),
  moon: <path d="M20.4 14.4A8.7 8.7 0 0 1 9.6 3.6a8.7 8.7 0 1 0 10.8 10.8Z" />,
  'magnifying-glass': (
    <>
      <circle cx="11" cy="11" r="6.4" />
      <path d="m15.7 15.7 4.3 4.3" />
    </>
  ),
  'sign-out': (
    <>
      <path d="M10 4H5.6A1.6 1.6 0 0 0 4 5.6v12.8A1.6 1.6 0 0 0 5.6 20H10" />
      <path d="m16 8.4 3.6 3.6-3.6 3.6" />
      <path d="M19.6 12H9" />
    </>
  ),

  // ── Mũi tên và dấu chỉ hướng ─────────────────────────────────────────────
  'caret-right': <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  'arrow-right': (
    <>
      <path d="M4 12h15.5" />
      <path d="m13.5 6 6 6-6 6" />
    </>
  ),
  'arrow-left': (
    <>
      <path d="M20 12H4.5" />
      <path d="m10.5 6-6 6 6 6" />
    </>
  ),
  'arrow-up': (
    <>
      <path d="M12 20V4.5" />
      <path d="m6 10.5 6-6 6 6" />
    </>
  ),
  'arrow-down': (
    <>
      <path d="M12 4v15.5" />
      <path d="m6 13.5 6 6 6-6" />
    </>
  ),
  'arrows-down-up': (
    <>
      <path d="M7.5 4v16" />
      <path d="m3.5 16 4 4 4-4" />
      <path d="M16.5 20V4" />
      <path d="m12.5 8 4-4 4 4" />
    </>
  ),
  'trend-up': (
    <>
      <path d="m3.5 16.5 5.8-5.8 3.6 3.6L20 7" />
      <path d="M14.6 7H20v5.4" />
    </>
  ),
  'trend-down': (
    <>
      <path d="m3.5 7.5 5.8 5.8 3.6-3.6L20 17" />
      <path d="M14.6 17H20v-5.4" />
    </>
  ),

  // ── Trạng thái ───────────────────────────────────────────────────────────
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11.4v5.2" />
      <circle cx="12" cy="8.2" r=".95" fill="currentColor" stroke="none" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4.3 2.95 19.2a1 1 0 0 0 .86 1.5h16.38a1 1 0 0 0 .86-1.5Z" />
      <path d="M12 10v4.3" />
      <circle cx="12" cy="17.5" r=".95" fill="currentColor" stroke="none" />
    </>
  ),
  prohibit: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m5.99 5.99 12.02 12.02" />
    </>
  ),
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.2 12.2 2.6 2.6 5-5.7" />
    </>
  ),
  x: <path d="M18 6 6 18M6 6l12 12" />,
} satisfies Record<string, ReactNode>;

/** Tên một hình trong bộ. Dùng làm kiểu cho `icon` của `lib/nav.ts`. */
export type IconName = keyof typeof GLYPHS;

/**
 * Một hình vẽ.
 *
 * Mặc định là hình TRANG TRÍ (`aria-hidden`): gần như mọi chỗ dùng đều có sẵn
 * chữ ngay bên cạnh, và để trình đọc màn hình đọc thêm tên hình ở đó là bắt
 * người dùng nghe hai lần cùng một thứ. Truyền `title` ở đúng những chỗ hình
 * đứng MỘT MÌNH — nút menu, nút đổi chủ đề — và nó thành `role="img"` kèm nhãn.
 *
 * `size` tính bằng px chứ không theo `em`: hình phải căn hàng với chữ ở cỡ
 * người gọi chọn, chứ không tự phình theo cỡ chữ của khối cha.
 */
export function Icon({
  name,
  size = 16,
  className,
  title,
}: {
  name: IconName;
  size?: number;
  className?: string;
  title?: string;
}): React.JSX.Element {
  const decorative = title === undefined;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      className={className}
    >
      {!decorative && <title>{title}</title>}
      {GLYPHS[name]}
    </svg>
  );
}
